import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../components/ThemeContext';
import { useResponsive } from '../components/useResponsive';
import { API_BASE } from '../../config';

// ============================================================
// TYPES
// ============================================================

interface StewardFinding {
  type: string;
  category: string;
  suggestion?: string;
  impact?: string;
  source?: string;
  factor?: string;
  result?: string;
  details?: string;
  ndc?: string;
  product_name?: string;
  severity?: string;
  [key: string]: unknown;
}

interface ProductFinding {
  id: number;
  ndc: string;
  product_name: string;
  manufacturer: string;
  coo_country: string;
  coo_score: number;
  quality_tier: string;
  reconciler_tier: string;
  reconciler_override: boolean;
  reconciler_conflict: boolean;
  findings: StewardFinding[];
  summary: string;
  data_discrepancies_count: number;
  data_verified_count: number;
  rescrape_flags_count: number;
  logic_adjustments_count: number;
  analyzed_at: string;
}

interface SummaryData {
  total_products_analyzed: number;
  total_data_discrepancies: number;
  total_data_verified: number;
  total_rescrape_flags: number;
  total_logic_adjustments: number;
  first_analyzed: string | null;
  last_analyzed: string | null;
}

// Raw finding row from the API (flat, not grouped by product)
interface ApiFindingRow {
  id: number;
  ndc_code: string;
  agent_id: string;
  finding_type: string;
  severity: string;
  title: string;
  description: string;
  details: Record<string, unknown>;
  resolved: boolean;
  resolved_at: string | null;
  scoring_cycle_id: string;
  created_at: string;
  // Enriched product metadata from swarm_scored_products
  product_name?: string | null;
  manufacturer?: string | null;
  coo_country?: string | null;
  coo_score?: number | null;
  quality_tier?: string | null;
  // Reconciler override / conflict data
  reconciler_tier?: string | null;
  reconciler_override?: boolean | null;
  reconciler_conflict?: boolean | null;
  reconciler_reason?: string | null;
  // Data Inspector summary
  di_summary?: string | null;
}

// Raw summary from the API
interface ApiSummary {
  agent_id: string;
  total: number;
  resolved: number;
  unresolved: number;
  by_type: Record<string, number>;
  by_severity: Record<string, number>;
  first_analyzed?: string | null;
  last_analyzed?: string | null;
}

// ============================================================
// STEWARD DISPLAY NAMES
// ============================================================

const STEWARD_DISPLAY: Record<string, string> = {
  steward_openfda_ndc: 'OpenFDA NDC',
  steward_fda_import_refusals: 'FDA Import Refusals',
  steward_epa_tri: 'EPA TRI',
  steward_fda_recalls: 'FDA Recalls',
  steward_dailymed_spl: 'DailyMed SPL',
  steward_package_ocr: 'Package OCR',
  steward_sec_edgar: 'SEC EDGAR',
  steward_drugs_at_fda: 'Drugs@FDA',
  steward_fda_registration: 'FDA Registration',
  steward_who_prequalification: 'WHO Prequalification',
  steward_dmf_pharmacompass: 'DMF PharmaCompass',
  steward_fda_ndc_directory: 'FDA NDC Directory',
  steward_fda_drug_shortages: 'FDA Drug Shortages',
  steward_pricing: 'Pricing (NADAC)',
  steward_rxnorm: 'RxNorm',
  steward_fda_label_api: 'FDA Label API',
  steward_haiku_spl: 'Haiku SPL Analysis',
  steward_legislation: 'Legislation',
  steward_fec_donations: 'FEC Donations',
  steward_lobbying: 'Lobbying (Senate LDA)',
  steward_politician_holdings: 'Politician Holdings',
  steward_pac_contributions: 'PAC Contributions',
  steward_ccp_flags: 'CCP Flags',
  steward_manufacturer_name_geo: 'Manufacturer Geo',
};

// ============================================================
// STEWARD-SPECIFIC FINDING TYPES
// ============================================================

const STEWARD_FINDING_TYPES: Array<{
  key: string;
  label: string;
  color: string;
  description: string;
}> = [
  { key: 'data_discrepancy', label: 'Discrepancies', color: '#EF4444', description: 'Contradictions or inconsistencies found in collected evidence data' },
  { key: 'data_verified', label: 'Verified', color: '#22C55E', description: 'All factors collected successfully with no discrepancies' },
  { key: 'rescrape_flag', label: 'Rescrape Flags', color: '#F59E0B', description: 'Data is stale, incomplete, or needs to be re-collected from the source' },
  { key: 'logic_adjustment', label: 'Logic Adjustments', color: '#06B6D4', description: 'Patterns of discrepancies or missing data suggest a possible logic adjustment' },
];

// ============================================================
// HELPERS
// ============================================================

function tierColor(tier: string): string {
  switch (tier?.toUpperCase()) {
    case 'HIGH': return '#22C55E';
    case 'MODERATE': return '#F59E0B';
    case 'LOW': return '#EF4444';
    default: return '#94A3B8';
  }
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return '--';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '--';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function findingTypeColor(t: string): string {
  const found = STEWARD_FINDING_TYPES.find(ft => ft.key === t);
  return found ? found.color : '#94A3B8';
}

function findingTypeLabel(t: string): string {
  const found = STEWARD_FINDING_TYPES.find(ft => ft.key === t);
  return found ? found.label : t.replace(/_/g, ' ');
}

function getTotalCount(summary: SummaryData | null, type: string): number {
  if (!summary) return 0;
  switch (type) {
    case 'data_discrepancy': return summary.total_data_discrepancies || 0;
    case 'data_verified': return summary.total_data_verified || 0;
    case 'rescrape_flag': return summary.total_rescrape_flags || 0;
    case 'logic_adjustment': return summary.total_logic_adjustments || 0;
    default: return 0;
  }
}

function getCountForProduct(pf: ProductFinding, type: string): number {
  switch (type) {
    case 'data_discrepancy': return pf.data_discrepancies_count || 0;
    case 'data_verified': return pf.data_verified_count || 0;
    case 'rescrape_flag': return pf.rescrape_flags_count || 0;
    case 'logic_adjustment': return pf.logic_adjustments_count || 0;
    default: return 0;
  }
}

/** Transform flat API summary into steward SummaryData shape. */
function transformApiSummary(raw: ApiSummary, productCount: number): SummaryData {
  const bt = raw.by_type || {};
  return {
    total_products_analyzed: productCount,
    total_data_discrepancies: bt.data_discrepancy || 0,
    total_data_verified: bt.data_verified || 0,
    total_rescrape_flags: bt.rescrape_flag || 0,
    total_logic_adjustments: bt.logic_adjustment || 0,
    first_analyzed: raw.first_analyzed || null,
    last_analyzed: raw.last_analyzed || null,
  };
}

/** Group flat API finding rows into steward ProductFinding[] grouped by NDC. */
function groupFindingsByProduct(rows: ApiFindingRow[]): ProductFinding[] {
  const byNdc = new Map<string, ApiFindingRow[]>();
  for (const row of rows) {
    const ndc = row.ndc_code || 'unknown';
    if (!byNdc.has(ndc)) byNdc.set(ndc, []);
    byNdc.get(ndc)!.push(row);
  }

  const result: ProductFinding[] = [];
  let idx = 0;
  for (const [ndc, ndcRows] of Array.from(byNdc.entries())) {
    const findings: StewardFinding[] = ndcRows.map(r => {
      const d = r.details || {};
      return {
        type: r.finding_type || (d.type as string) || '',
        category: (d.category as string) || '',
        suggestion: (d.suggestion as string) || r.description || '',
        impact: (d.impact as string) || undefined,
        source: (d.source as string) || undefined,
        factor: (d.factor as string) || undefined,
        result: (d.result as string) || undefined,
        details: r.description || undefined,
        severity: r.severity || undefined,
      };
    });

    const countType = (t: string) => findings.filter(f => f.type === t).length;
    const dates = ndcRows.map(r => r.created_at).filter(Boolean).sort();

    // Use enriched product metadata from the first row
    const firstRow = ndcRows[0];

    result.push({
      id: idx++,
      ndc,
      product_name: firstRow.product_name || '',
      manufacturer: firstRow.manufacturer || '',
      coo_country: firstRow.coo_country || '',
      coo_score: firstRow.coo_score || 0,
      quality_tier: firstRow.quality_tier || '',
      reconciler_tier: firstRow.reconciler_tier || '',
      reconciler_override: firstRow.reconciler_override || false,
      reconciler_conflict: firstRow.reconciler_conflict || false,
      findings,
      summary: firstRow.di_summary || '',
      data_discrepancies_count: countType('data_discrepancy'),
      data_verified_count: countType('data_verified'),
      rescrape_flags_count: countType('rescrape_flag'),
      logic_adjustments_count: countType('logic_adjustment'),
      analyzed_at: dates.length > 0 ? dates[dates.length - 1] : '',
    });
  }
  return result;
}

// ============================================================
// COMPONENT
// ============================================================

export default function StewardDataAnalyst() {
  const { agentName } = useParams<{ agentName: string }>();
  const navigate = useNavigate();
  const { T } = useTheme();
  const { isMobile } = useResponsive();

  const displayName = STEWARD_DISPLAY[agentName || ''] || (agentName || '').replace(/_/g, ' ');

  const [findings, setFindings] = useState<ProductFinding[]>([]);
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [filterCategory, setFilterCategory] = useState<string>('all');
  // Pagination
  const PAGE_SIZE = 50;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalFindings, setTotalFindings] = useState(0);

  // Data completeness
  const [dataCompleteness, setDataCompleteness] = useState<number | null>(null);

  // ── Fetch data (server-side filtering + pagination) ──
  const fetchData = useCallback(async (findingType?: string, page: number = 1) => {
    try {
      const offset = (page - 1) * PAGE_SIZE;
      let findingsUrl = `${API_BASE}/api/metasupply/swarm/data-analyst/findings?agent_id=${agentName}&limit=${PAGE_SIZE}&offset=${offset}`;
      if (findingType && findingType !== 'all') {
        findingsUrl += `&finding_type=${encodeURIComponent(findingType)}`;
      }
      const [findingsResp, summaryResp] = await Promise.all([
        fetch(findingsUrl, { credentials: 'include' }),
        fetch(`${API_BASE}/api/metasupply/swarm/data-analyst/summary?agent_id=${agentName}`, { credentials: 'include' }),
      ]);

      let hasError = false;

      let parsedFindings: ProductFinding[] = [];

      if (findingsResp.ok) {
        const data = await findingsResp.json();
        const items: ApiFindingRow[] = Array.isArray(data) ? data : (data.items || []);
        setTotalFindings(typeof data.total === 'number' ? data.total : items.length);
        // API returns a flat array — group by NDC then filter to steward-relevant types
        const allGrouped = groupFindingsByProduct(items);
        parsedFindings = allGrouped.map(pf => ({
          ...pf,
          findings: pf.findings.filter(f =>
            f.type === 'data_discrepancy' || f.type === 'data_verified' || f.type === 'rescrape_flag' || f.type === 'logic_adjustment'
          ),
        })).filter(pf => pf.findings.length > 0 || pf.data_discrepancies_count > 0 || pf.data_verified_count > 0 || pf.rescrape_flags_count > 0 || pf.logic_adjustments_count > 0);
        setFindings(parsedFindings);
      } else {
        setError('Failed to fetch analysis findings');
        hasError = true;
      }

      if (summaryResp.ok) {
        const rawSummary: ApiSummary = await summaryResp.json();
        const transformed = transformApiSummary(rawSummary, parsedFindings.length);
        setSummary(transformed);
      }

      // Fetch data completeness from the same endpoint used by dashboard tiles
      try {
        const compResp = await fetch(`${API_BASE}/api/metasupply/swarm/stewards/data-completeness`, { credentials: 'include' });
        if (compResp.ok) {
          const compData = await compResp.json();
          const stewardComp = agentName ? compData[agentName] : undefined;
          if (stewardComp && stewardComp.completeness_pct != null) {
            setDataCompleteness(stewardComp.completeness_pct);
          } else {
            setDataCompleteness(null);
          }
        }
      } catch { /* non-critical */ }

      if (!hasError) {
        setError(null);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [agentName]);

  useEffect(() => {
    fetchData(filterCategory, currentPage);
    const interval = setInterval(() => fetchData(filterCategory, currentPage), 30000);
    return () => clearInterval(interval);
  }, [fetchData, filterCategory, currentPage]);

  const toggleRow = (key: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Separate aggregate findings (logic adjustments) from per-product findings
  const aggregateFindings = findings.filter(pf => pf.ndc === '__aggregate__');
  const productFindings = findings.filter(pf => pf.ndc !== '__aggregate__');

  // Server-side filtering + pagination: findings are already filtered
  const filteredFindings = productFindings;
  const totalPages = Math.max(1, Math.ceil(totalFindings / PAGE_SIZE));

  const totalFindingsForProduct = (pf: ProductFinding): number => {
    return (pf.data_discrepancies_count || 0)
      + (pf.data_verified_count || 0)
      + (pf.rescrape_flags_count || 0)
      + (pf.logic_adjustments_count || 0);
  };

  // ── Loading state ──
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 40, height: 40, border: `3px solid ${T.border}`,
            borderTopColor: T.gold, borderRadius: '50%',
            animation: 'spin 1s linear infinite', margin: '0 auto 16px',
          }} />
          <span style={{ fontSize: 14, color: T.textLight }}>Loading steward analysis...</span>
        </div>
      </div>
    );
  }

  // ── Main render ──
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* ── Header ── */}
      <div>
        <button
          onClick={() => navigate(`/agent-detail/${agentName}`)}
          style={{
            border: 'none', background: 'none', cursor: 'pointer', fontSize: 13,
            color: T.navyLight, fontWeight: 600, padding: 0, fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to {displayName}
        </button>

        <div style={{
          background: `linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)`,
          borderRadius: 16, padding: isMobile ? '24px 16px' : '32px 40px',
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 20,
        }}>
          <div>
            <div style={{ marginBottom: 8 }}>
              <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color: '#FFFFFF', margin: 0 }}>
                Data Analyst &mdash; {displayName}
              </h1>
            </div>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', maxWidth: 600, lineHeight: 1.6, margin: 0 }}>
              Data quality analysis for the {displayName} steward agent.
              Tracks discrepancies, rescrape flags, logic adjustments, and overall data completeness.
            </p>
          </div>
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            background: 'rgba(255,255,255,0.08)', borderRadius: 12, padding: '16px 24px',
          }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: T.gold, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
              Analyzed
            </span>
            <span style={{ fontSize: 32, fontWeight: 800, color: '#FFFFFF' }}>
              {summary?.total_products_analyzed ?? findings.length}
            </span>
          </div>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '12px 16px', borderRadius: T.radiusSm,
          background: T.dangerBg, border: `1px solid ${T.danger}30`,
          color: T.danger, fontSize: 13, fontWeight: 600,
        }}>
          {error}
        </div>
      )}

      {/* ── Stat Tiles: 4 steward-specific metrics ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', gap: 12 }}>
        {STEWARD_FINDING_TYPES.map(ft => {
          const count = getTotalCount(summary, ft.key);
          return (
            <div
              key={ft.key}
              onClick={() => { const next = ft.key === filterCategory ? 'all' : ft.key; setFilterCategory(next); setCurrentPage(1); }}
              style={{
                background: T.white,
                border: `1px solid ${filterCategory === ft.key ? ft.color : T.border}`,
                borderRadius: T.radius,
                padding: 16, boxShadow: T.shadow, textAlign: 'center',
                cursor: 'pointer', transition: 'all 0.15s',
              }}
            >
              <div style={{
                fontSize: 10, fontWeight: 700, color: T.textLight,
                textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6,
              }}>
                {ft.label}
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, color: ft.color }}>
                {count}
              </div>
            </div>
          );
        })}

        {/* Data Completeness % tile */}
        <div
          style={{
            background: T.white,
            border: `1px solid ${T.border}`,
            borderRadius: T.radius,
            padding: 16, boxShadow: T.shadow, textAlign: 'center',
          }}
        >
          <div style={{
            fontSize: 10, fontWeight: 700, color: T.textLight,
            textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6,
          }}>
            Data Completeness
          </div>
          <div style={{
            fontSize: 28, fontWeight: 800,
            color: dataCompleteness === null ? T.textLight
              : dataCompleteness >= 90 ? '#22C55E'
              : dataCompleteness >= 70 ? '#F59E0B'
              : '#EF4444',
          }}>
            {dataCompleteness !== null ? `${dataCompleteness}%` : '--'}
          </div>
        </div>
      </div>

      {/* ── Filter Pills ── */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          onClick={() => { setFilterCategory('all'); setCurrentPage(1); }}
          style={{
            padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
            border: `1px solid ${filterCategory === 'all' ? T.navy : T.border}`,
            background: filterCategory === 'all' ? `${T.navy}12` : T.white,
            color: filterCategory === 'all' ? T.navy : T.textMid,
            cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
          }}
        >
          All
        </button>
        {STEWARD_FINDING_TYPES.map(ft => {
          const count = getTotalCount(summary, ft.key);
          const active = filterCategory === ft.key;
          return (
            <button
              key={ft.key}
              onClick={() => { setFilterCategory(ft.key); setCurrentPage(1); }}
              style={{
                padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                border: `1px solid ${active ? ft.color : T.border}`,
                background: active ? `${ft.color}12` : T.white,
                color: active ? ft.color : T.textMid,
                cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {ft.label}
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10,
                background: active ? `${ft.color}20` : `${T.navy}08`,
                color: active ? ft.color : T.textLight,
              }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Aggregate Logic Adjustments Banner ── */}
      {aggregateFindings.length > 0 && (filterCategory === 'all' || filterCategory === 'logic_adjustment') && (
        <div style={{
          background: T.white, border: `1px solid ${T.border}`,
          borderRadius: T.radius, boxShadow: T.shadow, overflow: 'hidden',
        }}>
          <div style={{
            padding: '16px 24px',
            borderBottom: `1px solid ${T.borderLight}`,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#06B6D4" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4M12 8h.01" />
            </svg>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.textDark }}>
              Cross-Product Pattern Analysis
            </span>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
              background: '#06B6D415', color: '#06B6D4',
            }}>
              {aggregateFindings.reduce((sum, pf) => sum + pf.findings.length, 0)} Logic Adjustments
            </span>
          </div>
          <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {aggregateFindings.flatMap(pf => pf.findings).map((finding, fIdx) => (
              <div key={fIdx} style={{
                padding: '14px 18px', borderRadius: T.radiusSm,
                background: T.offWhite, border: `1px solid ${T.borderLight}`,
                borderLeft: '4px solid #06B6D4',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <span style={{
                    fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.04em', padding: '2px 8px', borderRadius: 10,
                    background: '#06B6D415', color: '#06B6D4',
                  }}>
                    Logic Adjustment
                  </span>
                  {finding.severity && (
                    <span style={{
                      fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                      padding: '2px 8px', borderRadius: 10,
                      background: finding.severity === 'high' ? '#FEE2E2' : '#FEF3C7',
                      color: finding.severity === 'high' ? '#DC2626' : '#D97706',
                    }}>
                      {finding.severity}
                    </span>
                  )}
                  {finding.factor && (
                    <span style={{ fontSize: 11, fontWeight: 600, color: T.textMid }}>
                      Factor: {finding.factor}
                    </span>
                  )}
                </div>
                {finding.suggestion && (
                  <div style={{ fontSize: 13, color: T.textDark, lineHeight: 1.5 }}>
                    {finding.suggestion}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Results Table ── */}
      <div style={{
        background: T.white, border: `1px solid ${T.border}`,
        borderRadius: T.radius, boxShadow: T.shadow, overflow: 'hidden',
      }}>
        {filteredFindings.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>--</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.textDark, marginBottom: 8 }}>
              No Findings Yet
            </div>
            <div style={{ fontSize: 13, color: T.textLight, maxWidth: 400, margin: '0 auto' }}>
              The Data Analyst tracks discrepancies, rescrape flags, and logic adjustments
              for the {displayName} steward. Findings appear here once products are analyzed.
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                  {['NDC', 'Product Name', 'Manufacturer', 'COO', 'Score', 'Tier', 'Reconciler Tier', 'Findings', 'Analyzed'].map(col => (
                    <th key={col} style={{
                      padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700,
                      color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em',
                      whiteSpace: 'nowrap',
                    }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredFindings.map((pf, idx) => {
                  const rowKey = `${pf.ndc}-${idx}`;
                  const isExpanded = expandedRows.has(rowKey);
                  const displayFindings = filterCategory === 'all'
                    ? pf.findings
                    : pf.findings.filter(f => f.type === filterCategory);
                  const totalCount = totalFindingsForProduct(pf);

                  return (
                    <React.Fragment key={rowKey}>
                      <tr
                        onClick={() => toggleRow(rowKey)}
                        style={{
                          borderBottom: `1px solid ${T.borderLight}`,
                          cursor: 'pointer',
                          background: isExpanded ? `${T.gold}08` : 'transparent',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => { if (!isExpanded) (e.currentTarget as HTMLTableRowElement).style.background = `${T.navy}04`; }}
                        onMouseLeave={e => { if (!isExpanded) (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}
                      >
                        {/* NDC */}
                        <td style={{ padding: '12px 16px', fontWeight: 600, color: T.navy, whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <svg
                              width="14" height="14" viewBox="0 0 24 24" fill="none"
                              stroke={T.textLight} strokeWidth="2"
                              style={{
                                transition: 'transform 0.2s',
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                flexShrink: 0,
                              }}
                            >
                              <path d="M6 9l6 6 6-6" />
                            </svg>
                            {pf.ndc}
                          </div>
                        </td>
                        {/* Product Name */}
                        <td style={{ padding: '12px 16px', color: T.textDark, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {pf.product_name || '--'}
                        </td>
                        {/* Manufacturer */}
                        <td style={{ padding: '12px 16px', color: T.textDark }}>
                          {pf.manufacturer || '--'}
                        </td>
                        {/* COO */}
                        <td style={{ padding: '12px 16px', color: T.textDark }}>
                          {pf.coo_country || '--'}
                        </td>
                        {/* Tier (PRIMARY: scoring agent) */}
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{
                            fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                            letterSpacing: '0.04em', padding: '2px 8px', borderRadius: 10,
                            background: `${tierColor(pf.quality_tier)}18`,
                            color: tierColor(pf.quality_tier),
                          }}>
                            {pf.quality_tier || '--'}
                          </span>
                        </td>
                        {/* Tier — shows override badge if reconciler disagrees */}
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{
                              fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                              letterSpacing: '0.04em', padding: '2px 8px', borderRadius: 10,
                              background: `${tierColor(pf.reconciler_override ? pf.reconciler_tier : pf.quality_tier)}18`,
                              color: tierColor(pf.reconciler_override ? pf.reconciler_tier : pf.quality_tier),
                            }}>
                              {(pf.reconciler_override ? pf.reconciler_tier : pf.quality_tier) || '--'}
                            </span>
                            {pf.reconciler_override && (
                              <span title={`Scoring agent: ${pf.quality_tier} — Reconciler override: ${pf.reconciler_tier}`} style={{
                                fontSize: 8, fontWeight: 700, padding: '1px 5px', borderRadius: 6,
                                background: '#e11d4820', color: '#e11d48', cursor: 'help',
                              }}>
                                OVERRIDE
                              </span>
                            )}
                            {pf.reconciler_conflict && (
                              <span title="LLM disputes the reconciler's COO conclusion" style={{
                                fontSize: 8, fontWeight: 700, padding: '1px 5px', borderRadius: 6,
                                background: '#d9770620', color: '#d97706', cursor: 'help',
                              }}>
                                CONFLICT
                              </span>
                            )}
                          </div>
                        </td>
                        {/* Reconciler Confidence Tier */}
                        <td style={{ padding: '12px 16px' }}>
                          {pf.reconciler_tier ? (
                            <span style={{
                              fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                              letterSpacing: '0.04em', padding: '2px 8px', borderRadius: 10,
                              background: `${tierColor(pf.reconciler_tier)}18`,
                              color: tierColor(pf.reconciler_tier),
                            }}>
                              {pf.reconciler_tier}
                            </span>
                          ) : (
                            <span style={{ color: T.textLight, fontSize: 11 }}>--</span>
                          )}
                        </td>
                        {/* Findings count */}
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            {STEWARD_FINDING_TYPES.map(ft => {
                              const c = getCountForProduct(pf, ft.key);
                              if (c === 0) return null;
                              return (
                                <span key={ft.key} style={{
                                  fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                                  background: `${ft.color}15`, color: ft.color,
                                }}>
                                  {c} {ft.label}
                                </span>
                              );
                            })}
                            <span style={{
                              fontSize: 12, fontWeight: 700, color: T.textDark,
                              background: `${T.navy}08`, padding: '2px 10px', borderRadius: 10,
                            }}>
                              {totalCount}
                            </span>
                          </div>
                        </td>
                        {/* Analyzed */}
                        <td style={{ padding: '12px 16px', color: T.textLight, fontSize: 11, whiteSpace: 'nowrap' }}>
                          {formatTimestamp(pf.analyzed_at)}
                        </td>
                      </tr>

                      {/* ── Expanded Findings ── */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={9} style={{ padding: 0, background: T.offWhite }}>
                            <div style={{ padding: '16px 24px' }}>
                              {/* Summary */}
                              {pf.summary && (
                                <div style={{
                                  padding: '12px 16px', borderRadius: T.radiusSm,
                                  background: `${T.navy}06`, border: `1px solid ${T.navy}15`,
                                  marginBottom: 16, fontSize: 13, color: T.textDark, lineHeight: 1.6,
                                }}>
                                  <span style={{ fontWeight: 700, color: T.navy }}>Summary: </span>
                                  {pf.summary}
                                </div>
                              )}

                              {/* Individual findings */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {displayFindings.map((finding, fIdx) => (
                                  <div key={fIdx} style={{
                                    padding: '14px 18px', borderRadius: T.radiusSm,
                                    background: T.white, border: `1px solid ${T.borderLight}`,
                                    borderLeft: `4px solid ${findingTypeColor(finding.type)}`,
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                      <span style={{
                                        fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                                        letterSpacing: '0.04em', padding: '2px 8px', borderRadius: 10,
                                        background: `${findingTypeColor(finding.type)}15`,
                                        color: findingTypeColor(finding.type),
                                      }}>
                                        {findingTypeLabel(finding.type)}
                                      </span>
                                      {finding.source && (
                                        <span style={{ fontSize: 11, color: T.textLight }}>
                                          Source: {finding.source}
                                        </span>
                                      )}
                                      {finding.severity && (
                                        <span style={{
                                          fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                                          padding: '2px 8px', borderRadius: 10,
                                          background: finding.severity === 'high' ? '#FEE2E2' : finding.severity === 'medium' ? '#FEF3C7' : '#E0F2FE',
                                          color: finding.severity === 'high' ? '#DC2626' : finding.severity === 'medium' ? '#D97706' : '#0284C7',
                                        }}>
                                          {finding.severity}
                                        </span>
                                      )}
                                    </div>
                                    {finding.suggestion && (
                                      <div style={{ fontSize: 13, color: T.textDark, lineHeight: 1.5 }}>
                                        {finding.suggestion}
                                      </div>
                                    )}
                                    {finding.result && (
                                      <div style={{ fontSize: 11, color: T.textMid, lineHeight: 1.5, marginTop: 4 }}>
                                        <span style={{ fontWeight: 600 }}>Evidence: </span>
                                        {String(finding.result)}
                                      </div>
                                    )}
                                    {finding.impact && (
                                      <div style={{ fontSize: 11, color: T.textLight, marginTop: 6 }}>
                                        Impact: {finding.impact}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>

                              {displayFindings.length === 0 && (
                                <div style={{ padding: 16, textAlign: 'center', color: T.textLight, fontSize: 13 }}>
                                  No findings in this category for this product.
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Pagination Controls ── */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
          <span style={{ fontSize: 13, color: T.textLight }}>
            Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, totalFindings)} of {totalFindings} findings
          </span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              style={{
                padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                border: `1px solid ${T.border}`, background: T.white, color: T.textMid,
                cursor: currentPage <= 1 ? 'not-allowed' : 'pointer', opacity: currentPage <= 1 ? 0.5 : 1,
                fontFamily: 'inherit',
              }}
            >
              ← Prev
            </button>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.textDark, minWidth: 80, textAlign: 'center' }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              style={{
                padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                border: `1px solid ${T.border}`, background: T.white, color: T.textMid,
                cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages ? 0.5 : 1,
                fontFamily: 'inherit',
              }}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* ── Finding Types Legend ── */}
      <div style={{
        background: T.white, border: `1px solid ${T.border}`,
        borderRadius: T.radius, boxShadow: T.shadow, padding: isMobile ? '20px 16px' : '24px 28px',
      }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: T.textDark, margin: '0 0 16px 0' }}>
          Finding Types
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 12 }}>
          {STEWARD_FINDING_TYPES.map(ft => (
            <div key={ft.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{
                width: 10, height: 10, borderRadius: '50%', background: ft.color,
                flexShrink: 0, marginTop: 4,
              }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: ft.color }}>
                  {ft.label}
                </div>
                <div style={{ fontSize: 11, color: T.textLight, lineHeight: 1.4 }}>
                  {ft.description}
                </div>
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <div style={{
              width: 10, height: 10, borderRadius: '50%', background: '#22C55E',
              flexShrink: 0, marginTop: 4,
            }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#22C55E' }}>
                Data Completeness %
              </div>
              <div style={{ fontSize: 11, color: T.textLight, lineHeight: 1.4 }}>
                Percentage of tracked products with complete, non-discrepant data from this steward
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
